import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GenericValidationsService {

  constructor(private fb: FormBuilder) {}


  private progressPercentageSource = new BehaviorSubject<number>(0); // Default value
  progressPercentage$ = this.progressPercentageSource.asObservable();

  // Method to update the progress percentage
  setProgressPercentage(value: number): void {
    this.progressPercentageSource.next(value);
  }

  // Method to get the current value (optional if needed)
  getProgressPercentage(): number {
    return this.progressPercentageSource.getValue();
  }







  private headerTitleSource = new BehaviorSubject<string>('Default Header');
  private subHeaderTitleSource = new BehaviorSubject<string>('Default SubHeader');

  headerTitle$ = this.headerTitleSource.asObservable();
  subHeaderTitle$ = this.subHeaderTitleSource.asObservable();

  setHeader(title: string, subTitle: string) {
    this.headerTitleSource.next(title);
    this.subHeaderTitleSource.next(subTitle);
  }




  // Function to allow point value
  onlyNumberValidator(event: KeyboardEvent) {
    // point value are accepted
    const keyCode = event.keyCode;
    // Allow digits, backspace, and decimal point
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 46 && keyCode !== 8) {
      event.preventDefault();
    }
    // Prevent more than one decimal point
    const input = event.target as HTMLInputElement;
    if (input.value.includes('.') && keyCode === 46) {
      event.preventDefault();
    }
  }

  // Function to not allow point value
  onlyNumberWithoutPointValidator(event: KeyboardEvent) {
  // point value are not  accepted
    const input = event.target as HTMLInputElement;
    const key = event.key;

    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', // Navigation and control keys
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9' // Number keys
    ];
    // Prevent all other keys if not in allowed keys
    if (!allowedKeys.includes(key)) {
      event.preventDefault();
    }
  }

  // Function to allow only alphabetic letters (A-Z, a-z)
  onlyLettersValidator(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    // Allow uppercase (A-Z) and lowercase (a-z) letters and backspace
    if ((keyCode < 65 || keyCode > 90) && (keyCode < 97 || keyCode > 122) && keyCode !== 8) {
      event.preventDefault();
    }
  }

  setupFormListeners(
    form: FormGroup,
    controlMappings: { [key: string]: string[] },
    isShowOn: boolean
  ): void {
    const dynamicControls = Object.keys(controlMappings);

    // Dynamically apply listeners based on controlMappings keys
    dynamicControls.forEach(controlName => {
      const control = form.get(controlName);

      if (control) {
        control.valueChanges.subscribe((value) => {
          const dependentControlNames = controlMappings[controlName] || [];

          // Conditional check based on isShowOn
          if ((value === true && isShowOn) || (value !== true && !isShowOn)) {
            // Show dependent controls and set validators
            dependentControlNames.forEach(dependentControlName => {
              if (!form.contains(dependentControlName)) {
                form.addControl(dependentControlName, this.fb.control('', Validators.required));
              }
              const dependentControl = form.get(dependentControlName);
              if (dependentControl) {
                dependentControl.setValidators([Validators.required]);
                dependentControl.updateValueAndValidity();
              }
            });
          } else {
            // Hide dependent controls and clear validators
            dependentControlNames.forEach(dependentControlName => {
              const dependentControl = form.get(dependentControlName);
              if (dependentControl) {
                dependentControl.clearValidators();
                dependentControl.setValue('');
                dependentControl.updateValueAndValidity();
              }
            });
          }
        });
      }
    });
  }


 // Format date function
 formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// validator Validator to check for whitespace
noWhitespaceValidator(control: AbstractControl): { [key: string]: any } | null {
  const isWhitespace = (control.value || '').trim().length === 0;
  return isWhitespace ? { whitespace: true } : null;
}


 // Form submit Function to remove whitespaces from string values in an object
 removeWhitespaceFromValues(formValues: any): any {
  const cleanedValues = { ...formValues };
  Object.keys(cleanedValues).forEach(key => {
    if (typeof cleanedValues[key] === 'string') {
      cleanedValues[key] = cleanedValues[key].trim();  // Remove leading/trailing spaces
    }
  });
  return cleanedValues;
}



generateYearRange(): number[] {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - environment.yearRange.past; // Use value from environment
  const endYear = currentYear + environment.yearRange.future; // Use value from environment

  const years: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }
  return years;
}
















  /**
   * Generic checkbox validator
   * Validates only when SaveMode == SAVE
   * @param saveModeControlName - the name of the control holding SaveMode
   * @param expectedValue - value of SaveMode to validate against (e.g., "SAVE")
   */
  checkboxValidator(saveModeControlName: string, expectedValue: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null; // parent not ready

      const saveMode = control.parent.get(saveModeControlName)?.value;

      if (saveMode === expectedValue && control.value !== true) {
        return { checkboxRequired: true };
      }

      return null;
    };
  }

  /**
   * Check if checkbox error should be shown
   * @param form - the form group
   * @param checkboxControlName - checkbox control name
   * @param saveModeControlName - save mode control name
   * @param expectedValue - expected SaveMode (e.g., "SAVE")
   * @param submitted - submitted flag from component
   */
  shouldShowCheckboxValidation(
    form: any,
    checkboxControlName: string,
    saveModeControlName: string,
    expectedValue: any,
    submitted: boolean
  ): boolean {
    if (!form) return false;

    const saveMode = form.get(saveModeControlName)?.value;
    const checkboxControl = form.get(checkboxControlName);

    return (
      submitted &&
      saveMode === expectedValue &&
      checkboxControl?.invalid === true
    );
  }





  filterMenuBasedOnPermissionsOld(menu: any[], permission: any): any[] {
    if (!permission?.RoleMenuPermissions) return [];

    return menu.filter((menuItem: any) => {
      const roles = Array.isArray(menuItem.role) ? menuItem.role : [menuItem.role];

      // Check if any role has permission
      return roles.some((role: string) => {
        const perm = permission.RoleMenuPermissions.find(
          (p: { role_key: string }) => p.role_key === role
        );
        return perm && perm.can_see_list  
      });
    });
  }
  



  filterMenuBasedOnPermissions(menu: any[], permission: any): any[] {
    if (!permission?.RoleMenuPermissions) return [];
  
    return menu.filter((menuItem: any) => {
      const roles = Array.isArray(menuItem.role) ? menuItem.role : [menuItem.role];
  
      return roles.some((role: string) => {
        const perm = permission.RoleMenuPermissions.find(
          (p: { role_key: string }) => p.role_key === role
        );
  
        if (!perm) return false;
  
        // 🚩 Special rule: If menu is a Dashboard, check `can_see_dashboard`
        // if (menuItem.title === 'Dashboard') {
        //   return perm.can_see_dashboard === true;
        // }
  
        // ✅ For all other menus, check `can_see_list`
        return perm.can_see_list === true;
      });
    });
  }

}
